import { useMarketRealtimeStore } from '@/hooks/store/useMarketRealtimeStore';

const MARKET_WS_URL = 'wss://ws-subscriptions-clob.polymarket.com/ws/market';
const CLOB_BOOK_URL = 'https://clob.polymarket.com/book';
const PRICE_FLUSH_INTERVAL_MS = 500;
const BEST_BID_ASK_FALLBACK_AFTER_MS = 5000;
const BOOK_SNAPSHOT_REFRESH_INTERVAL_MS = 15000;
const HEARTBEAT_INTERVAL_MS = 10000;

type WsStatus = 'idle' | 'connecting' | 'connected' | 'closed';
type PriceUpdateSource =
  | 'book_snapshot'
  | 'ws_book'
  | 'ws_best_bid_ask'
  | 'ws_last_trade_price'
  | 'ws_price_change';

interface PendingPriceUpdate {
  tokenId: string;
  price: number;
  source: PriceUpdateSource;
  bestBid?: number;
  bestAsk?: number;
  lastTradePrice?: number;
}

interface OrderBookLevel {
  price?: string | number;
  size?: string | number;
}

interface OrderBookSnapshot {
  asset_id?: string;
  assetId?: string;
  bids?: OrderBookLevel[];
  asks?: OrderBookLevel[];
  last_trade_price?: string | number;
  lastTradePrice?: string | number;
}

function normalizeTokenId(value: unknown): string {
  return String(value ?? '').trim();
}

function parseNumber(value: unknown): number | undefined {
  const num = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
  return Number.isFinite(num) ? num : undefined;
}

function getBestBid(levels?: OrderBookLevel[]): number | undefined {
  const prices =
    levels
      ?.map((level) => parseNumber(level.price))
      .filter((price): price is number => price !== undefined) ?? [];

  return prices.length ? Math.max(...prices) : undefined;
}

function getBestAsk(levels?: OrderBookLevel[]): number | undefined {
  const prices =
    levels
      ?.map((level) => parseNumber(level.price))
      .filter((price): price is number => price !== undefined) ?? [];

  return prices.length ? Math.min(...prices) : undefined;
}

function getTokenId(payload: Record<string, unknown>): string {
  return normalizeTokenId(
    payload.asset_id ?? payload.assetId ?? payload.token_id ?? payload.tokenId,
  );
}

function getEventType(payload: Record<string, unknown>): string {
  return String(payload.event_type ?? payload.type ?? '').trim();
}

class PolymarketMarketWsManager {
  private ws: WebSocket | null = null;
  private status: WsStatus = 'idle';
  private refCounts = new Map<string, number>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private intentionallyClosed = false;
  private pendingPriceUpdates = new Map<string, PendingPriceUpdate>();
  private lastTradeTimestamps = new Map<string, number>();
  private bookSnapshotFetchedAt = new Map<string, number>();
  private bookSnapshotInFlight = new Set<string>();
  /** Token subscription or WebSocket activity invalidates older REST book responses. */
  private tokenRevisions = new Map<string, number>();

  subscribe(tokenIds: string[]) {
    const normalized = this.normalizeTokenIds(tokenIds);
    if (normalized.length === 0) return;

    const added: string[] = [];
    normalized.forEach((tokenId) => {
      const previous = this.refCounts.get(tokenId) ?? 0;
      if (previous === 0) {
        added.push(tokenId);
        this.bumpTokenRevision(tokenId);
      }
      this.refCounts.set(tokenId, previous + 1);
    });

    this.ensureConnected();
    this.sendSubscription(added, 'subscribe');
    this.fetchBookSnapshots(normalized);
  }

  release(tokenIds: string[]) {
    const normalized = this.normalizeTokenIds(tokenIds);
    if (normalized.length === 0) return;

    const removed: string[] = [];
    normalized.forEach((tokenId) => {
      const previous = this.refCounts.get(tokenId) ?? 0;
      if (previous === 0) return;
      const nextCount = previous - 1;
      if (nextCount <= 0) {
        this.refCounts.delete(tokenId);
        removed.push(tokenId);
        this.bumpTokenRevision(tokenId);
        this.bookSnapshotFetchedAt.delete(tokenId);
        this.lastTradeTimestamps.delete(tokenId);
        if (!this.bookSnapshotInFlight.has(tokenId)) this.tokenRevisions.delete(tokenId);
      } else {
        this.refCounts.set(tokenId, nextCount);
      }
    });

    if (this.refCounts.size === 0) {
      this.close();
      return;
    }

    this.sendSubscription(removed, 'unsubscribe');
  }

  private normalizeTokenIds(tokenIds: string[]): string[] {
    return Array.from(new Set(tokenIds.map(normalizeTokenId).filter(Boolean)));
  }

  private bumpTokenRevision(tokenId: string) {
    this.tokenRevisions.set(tokenId, (this.tokenRevisions.get(tokenId) ?? 0) + 1);
  }

  private ensureConnected() {
    if (this.status === 'connected' || this.status === 'connecting') return;
    if (this.refCounts.size === 0) return;

    this.intentionallyClosed = false;
    this.status = 'connecting';

    try {
      const socket = new WebSocket(MARKET_WS_URL);
      this.ws = socket;
      socket.onopen = () => {
        if (this.ws !== socket) return;
        this.status = 'connected';
        this.reconnectAttempts = 0;
        this.sendSubscription();
        this.startHeartbeat();
      };
      socket.onmessage = (event) => {
        if (this.ws !== socket) return;
        this.handleMessage(event.data);
      };
      socket.onerror = (event) => {
        console.warn('[PolymarketMarketWS] error:', event);
      };
      socket.onclose = () => {
        if (this.ws !== socket) return;
        this.stopHeartbeat();
        this.status = 'closed';
        this.ws = null;
        if (!this.intentionallyClosed && this.refCounts.size > 0) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.warn('[PolymarketMarketWS] connect failed:', error);
      this.status = 'closed';
      this.scheduleReconnect();
    }
  }

  private close() {
    this.intentionallyClosed = true;
    this.status = 'closed';
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // no-op
      }
      this.ws = null;
    }
    this.clearPendingUpdates();
  }

  private scheduleReconnect() {
    if (this.reconnectTimer || this.refCounts.size === 0) return;

    const delay = Math.min(30000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnected();
    }, delay);
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send('PING');
        } catch (error) {
          console.warn('[PolymarketMarketWS] heartbeat failed:', error);
        }
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private sendSubscription(
    assetIds = Array.from(this.refCounts.keys()),
    operation?: 'subscribe' | 'unsubscribe',
  ) {
    if (!this.ws || this.status !== 'connected' || this.ws.readyState !== WebSocket.OPEN) return;
    if (assetIds.length === 0) return;

    try {
      this.ws.send(
        JSON.stringify(
          operation
            ? { assets_ids: assetIds, operation, custom_feature_enabled: true }
            : { assets_ids: assetIds, type: 'market', custom_feature_enabled: true },
        ),
      );
    } catch (error) {
      console.warn('[PolymarketMarketWS] subscription failed:', error);
    }
  }

  private handleMessage(rawData: unknown) {
    try {
      if (typeof rawData === 'string') {
        const messageText = rawData.trim();
        if (!messageText) return;

        const upperMessageText = messageText.toUpperCase();
        if (['PING', 'PONG'].includes(upperMessageText)) {
          return;
        }

        if (!messageText.startsWith('{') && !messageText.startsWith('[')) {
          return;
        }
      }

      const parsed = typeof rawData === 'string' ? JSON.parse(rawData.trim()) : rawData;
      const messages = Array.isArray(parsed) ? parsed : [parsed];

      messages.forEach((message) => {
        if (!message || typeof message !== 'object') return;
        this.handlePayload(message as Record<string, unknown>);
      });
    } catch (error) {
      console.warn('[PolymarketMarketWS] message parse failed:', error);
    }
  }

  private handlePayload(payload: Record<string, unknown>) {
    const eventType = getEventType(payload);
    const tokenId = getTokenId(payload);
    const store = useMarketRealtimeStore.getState();

    if (eventType === 'new_market') {
      return;
    }

    if (eventType === 'market_resolved') {
      const winningAssetId = normalizeTokenId(payload.winning_asset_id ?? payload.winningAssetId);
      store.markMarketResolved({
        assetId: winningAssetId || tokenId,
        conditionId: normalizeTokenId(payload.condition_id ?? payload.conditionId),
        resolvedOutcome:
          payload.winning_outcome == null
            ? payload.resolved_outcome == null
              ? null
              : String(payload.resolved_outcome)
            : String(payload.winning_outcome),
      });
      return;
    }

    if (eventType === 'best_bid_ask') {
      const bestBid = parseNumber(payload.best_bid ?? payload.bid);
      const bestAsk = parseNumber(payload.best_ask ?? payload.ask);
      const mid =
        bestBid !== undefined && bestAsk !== undefined
          ? (bestBid + bestAsk) / 2
          : (bestBid ?? bestAsk);

      if (tokenId && mid !== undefined) {
        const lastTradeAt = this.lastTradeTimestamps.get(tokenId) ?? 0;
        if (Date.now() - lastTradeAt > BEST_BID_ASK_FALLBACK_AFTER_MS) {
          this.queuePriceUpdate({
            tokenId,
            price: mid,
            source: 'ws_best_bid_ask',
            bestBid,
            bestAsk,
          });
        }
      }
      return;
    }

    if (eventType === 'book') {
      this.handleBookSnapshot(payload as OrderBookSnapshot, 'ws_book');
      return;
    }

    if (eventType === 'last_trade_price') {
      const price = parseNumber(payload.price ?? payload.last_trade_price);
      if (tokenId && price !== undefined) {
        this.lastTradeTimestamps.set(tokenId, Date.now());
        this.queuePriceUpdate({
          tokenId,
          price,
          source: 'ws_last_trade_price',
          lastTradePrice: price,
        });
      }
      return;
    }

    if (eventType === 'price_change') {
      const changes = payload.price_changes ?? payload.priceChanges;
      if (!Array.isArray(changes)) return;
      changes.forEach((change: unknown) => {
        if (!change || typeof change !== 'object') return;
        const priceChange = change as Record<string, unknown>;
        const changedTokenId = getTokenId(priceChange);
        const bestBid = parseNumber(priceChange.best_bid ?? priceChange.bestBid);
        const bestAsk = parseNumber(priceChange.best_ask ?? priceChange.bestAsk);
        const mid =
          bestBid !== undefined && bestAsk !== undefined
            ? (bestBid + bestAsk) / 2
            : (bestBid ?? bestAsk);
        if (!changedTokenId || mid === undefined) return;
        const lastTradeAt = this.lastTradeTimestamps.get(changedTokenId) ?? 0;
        if (Date.now() - lastTradeAt <= BEST_BID_ASK_FALLBACK_AFTER_MS) return;
        this.queuePriceUpdate({
          tokenId: changedTokenId,
          price: mid,
          source: 'ws_price_change',
          bestBid,
          bestAsk,
        });
      });
      return;
    }
  }

  private handleBookSnapshot(snapshot: OrderBookSnapshot, source: PriceUpdateSource) {
    const tokenId = normalizeTokenId(snapshot.asset_id ?? snapshot.assetId);
    if (!tokenId) return;

    const bestBid = getBestBid(snapshot.bids);
    const bestAsk = getBestAsk(snapshot.asks);
    const mid =
      bestBid !== undefined && bestAsk !== undefined
        ? (bestBid + bestAsk) / 2
        : (bestBid ?? bestAsk);
    const lastTradePrice = parseNumber(snapshot.last_trade_price ?? snapshot.lastTradePrice);

    if (mid === undefined) return;

    this.queuePriceUpdate({
      tokenId,
      price: mid,
      source,
      bestBid,
      bestAsk,
      lastTradePrice,
    });
  }

  private fetchBookSnapshots(tokenIds: string[]) {
    tokenIds.forEach((tokenId) => {
      const lastFetchedAt = this.bookSnapshotFetchedAt.get(tokenId) ?? 0;
      if (Date.now() - lastFetchedAt < BOOK_SNAPSHOT_REFRESH_INTERVAL_MS) return;
      if (this.bookSnapshotInFlight.has(tokenId)) return;

      this.bookSnapshotInFlight.add(tokenId);
      const revisionAtRequest = this.tokenRevisions.get(tokenId);

      const url = `${CLOB_BOOK_URL}?token_id=${encodeURIComponent(tokenId)}`;
      fetch(url)
        .then((response) => {
          if (
            !this.refCounts.has(tokenId) ||
            this.tokenRevisions.get(tokenId) !== revisionAtRequest
          )
            return null;
          this.bookSnapshotFetchedAt.set(tokenId, Date.now());
          if (response.status === 404) {
            return null;
          }
          if (!response.ok) {
            throw new Error(`CLOB book request failed: ${response.status}`);
          }
          return response.json() as Promise<OrderBookSnapshot>;
        })
        .then((snapshot) => {
          if (!snapshot) return;
          if (
            !this.refCounts.has(tokenId) ||
            this.tokenRevisions.get(tokenId) !== revisionAtRequest
          )
            return;
          this.handleBookSnapshot(
            {
              ...snapshot,
              asset_id: snapshot.asset_id ?? tokenId,
            },
            'book_snapshot',
          );
        })
        .catch((error) => {
          console.warn('[PolymarketMarketWS] book snapshot failed:', error);
        })
        .finally(() => {
          this.bookSnapshotInFlight.delete(tokenId);
          if (!this.refCounts.has(tokenId)) this.tokenRevisions.delete(tokenId);
        });
    });
  }

  private queuePriceUpdate(update: PendingPriceUpdate) {
    if (update.source !== 'book_snapshot') this.bumpTokenRevision(update.tokenId);
    this.pendingPriceUpdates.set(update.tokenId, update);

    if (this.flushTimer) return;

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flushPriceUpdates();
    }, PRICE_FLUSH_INTERVAL_MS);
  }

  private flushPriceUpdates() {
    if (this.pendingPriceUpdates.size === 0) return;

    const updates = Array.from(this.pendingPriceUpdates.values());
    this.pendingPriceUpdates.clear();

    const store = useMarketRealtimeStore.getState();

    updates.forEach((update) => {
      store.updateTokenPrice(update.tokenId, update.price, {
        bestBid: update.bestBid,
        bestAsk: update.bestAsk,
        lastTradePrice: update.lastTradePrice,
        lastUpdateSource: update.source,
      });
    });
  }

  private clearPendingUpdates() {
    this.pendingPriceUpdates.clear();
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}

export const polymarketMarketWsManager = new PolymarketMarketWsManager();
